// LotLingo Portal - Distance Calculation Module
// Handles ZIP/postal code extraction and Haversine distance calculations

import { escapeHtml } from './formatting';
import { Lot } from './types';
import { createLogger } from '../../shared/logger.js';

const log = createLogger('portal:distance');

// Runtime initialization (types declared centrally in global.d.ts)
if (!window.__distanceCalculationsStarted) {
  window.__distanceCalculationsStarted = new Set<string>();
}
if (!window.__distanceCache) {
  window.__distanceCache = {} as Record<string, number | 'pending' | null>;
}

/**
 * Extract US ZIP code from location string
 */
export function extractUsZip(location: string | null | undefined): string | null {
  if (!location) return null;
  // Match 5-digit ZIP, optionally followed by -#### (ZIP+4)
  const zipPattern = /([0-9]{5})(?:-[0-9]{4})?/g;
  const matches = location.match(zipPattern);
  if (matches && matches.length > 0) {
    return matches[0].substring(0, 5);
  }
  return null;
}

/**
 * Extract Canadian postal code from location string
 */
export function extractCanadianPostal(location: string | null | undefined): string | null {
  if (!location) return null;
  // Accept full postal codes (A1A 1A1 / A1A1A1) OR just the FSA (A1A)
  const fullPattern = /([A-Z][0-9][A-Z])[ ]?([0-9][A-Z][0-9])?/i;
  const m = location.match(fullPattern);
  if (m) {
    const first = m[1].toUpperCase();
    const last = m[2] ? m[2].toUpperCase() : null;
    return last ? first + last : first;
  }
  return null;
}

/**
 * Extract ZIP or postal code from location (prefers CA to avoid misclassification)
 */
export function extractZipOrPostalCode(
  location: string | null | undefined
): { code: string; country: 'CA' | 'US' } | null {
  const ca = extractCanadianPostal(location);
  if (ca) {
    return { code: ca, country: 'CA' };
  }
  const us = extractUsZip(location);
  if (us) {
    return { code: us, country: 'US' };
  }
  return null;
}

/**
 * Extract City, State, ZIP for display
 */
export function extractCityStateZip(location: string | null | undefined): string | null {
  if (!location) return null;

  const cityStateZipPattern = /([A-Za-z0-9 ]+),[ ]*([A-Z]{2})[ ]*([0-9]{5})(?:-[0-9]{4})?/i;
  const match = location.match(cityStateZipPattern);

  if (match) {
    const city = match[1].trim();
    const state = match[2].toUpperCase();
    const zip = match[3];
    return `${city}, ${state} ${zip}`;
  }

  // Fallback: state and ZIP only
  const stateZipPattern = /([A-Z]{2})[ ]*([0-9]{5})(?:-[0-9]{4})?/i;
  const stateZipMatch = location.match(stateZipPattern);
  if (stateZipMatch) {
    const state = stateZipMatch[1].toUpperCase();
    const zip = stateZipMatch[2];
    return `${state} ${zip}`;
  }

  return null;
}

/**
 * Get preferred distance units (mi or km)
 */
export function getPreferredDistanceUnits(
  homeZip: string,
  settings: { distanceUnits?: string } | null | undefined
): 'mi' | 'km' {
  const units = settings && settings.distanceUnits ? settings.distanceUnits : 'auto';
  if (units === 'mi' || units === 'km') {
    return units;
  }
  // auto: infer from homeZip (CA => km, else mi)
  const compact = (homeZip || '').toString().replace(/\s+/g, '').toUpperCase();
  const isHomeCA = /^[A-Z][0-9][A-Z][0-9][A-Z][0-9]$/.test(compact);
  return isHomeCA ? 'km' : 'mi';
}

/**
 * Calculate Haversine distance between two ZIP/postal codes
 */
export async function calculateDistance(
  homeZip: string,
  lotCode: string,
  saveToBackend?: () => Promise<any>
): Promise<number | null> {
  if (!homeZip || !lotCode) return null;
  const cacheKey = `${homeZip}-${lotCode}`;

  // Normalize inputs
  let trimmedHome = String(homeZip).trim();
  let trimmedLot = String(lotCode).trim();

  // US ZIP: 5 digits
  const homeIsUs = /^[0-9]{5}(?:-[0-9]{4})?$/.test(trimmedHome);
  if (homeIsUs && trimmedHome.length > 5) {
    trimmedHome = trimmedHome.substring(0, 5);
  }

  // CA postal: Full or FSA only
  const homeIsCa = !homeIsUs && /^[A-Z][0-9][A-Z](?:[ ]?[0-9][A-Z][0-9])?$/i.test(trimmedHome);
  if (homeIsCa) {
    trimmedHome = trimmedHome.replace(/[ ]+/g, '').toUpperCase();
  }

  const lotIsUs = /^[0-9]{5}$/.test(trimmedLot);
  const lotIsCa = !lotIsUs && /^[A-Z][0-9][A-Z](?:[ ]?[0-9][A-Z][0-9])?$/i.test(trimmedLot);
  if (lotIsCa) {
    trimmedLot = trimmedLot.replace(/[ ]+/g, '').toUpperCase();
  }

  // Build API endpoints (Zippopotam.us expects FSA for Canadian codes)
  const homeEndpoint = homeIsUs
    ? `us/${trimmedHome}`
    : homeIsCa
      ? `ca/${trimmedHome.substring(0, 3)}`
      : null;
  const lotEndpoint = lotIsUs
    ? `us/${trimmedLot}`
    : lotIsCa
      ? `ca/${trimmedLot.substring(0, 3)}`
      : null;

  if (!homeEndpoint || !lotEndpoint) {
    log.warn(`Invalid endpoints for ${cacheKey}`);
    return null;
  }

  try {
    const responses = await Promise.all([
      fetch(`https://api.zippopotam.us/${homeEndpoint}`)
        .then(r => (r.ok ? r.json() : null))
        .catch(() => null),
      fetch(`https://api.zippopotam.us/${lotEndpoint}`)
        .then(r => (r.ok ? r.json() : null))
        .catch(() => null),
    ]);

    const loc1 = responses[0];
    const loc2 = responses[1];

    if (!loc1 || !loc2 || !loc1.places || !loc1.places[0] || !loc2.places || !loc2.places[0]) {
      if (typeof window.__distanceCache === 'undefined') {
        window.__distanceCache = {};
      }
      window.__distanceCache[cacheKey] = null;
      return null;
    }

    const lat1 = parseFloat(loc1.places[0].latitude);
    const lon1 = parseFloat(loc1.places[0].longitude);
    const lat2 = parseFloat(loc2.places[0].latitude);
    const lon2 = parseFloat(loc2.places[0].longitude);

    // Haversine formula
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const R = 3958.8; // miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = Math.round(R * c);

    if (typeof window.__distanceCache === 'undefined') {
      window.__distanceCache = {};
    }
    window.__distanceCache[cacheKey] = distance;

    // Save to backend if callback provided
    if (typeof saveToBackend === 'function') {
      saveToBackend().catch(err => {
        log.warn('Failed to save cache to backend:', err);
      });
    }

    return distance;
  } catch (e) {
    log.error(`Error calculating distance for ${cacheKey}:`, e);
    if (typeof window.__distanceCache === 'undefined') {
      window.__distanceCache = {};
    }
    window.__distanceCache[cacheKey] = null;
    return null;
  }
}

/**
 * Trigger distance calculations for all lots
 */
export function triggerDistanceCalculations(
  lots: Lot[],
  homeZip: string,
  settings?: { distanceUnits?: string } | null,
  saveToBackend?: () => Promise<any>,
  renderCallback?: () => void
): void {
  if (!homeZip || !Array.isArray(lots) || lots.length === 0) return;

  if (typeof window.__distanceCache === 'undefined') {
    window.__distanceCache = {};
  }

  if (typeof window.__distanceCalculationsStarted === 'undefined') {
    window.__distanceCalculationsStarted = new Set();
  }

  lots.forEach(function (lot) {
    if (!lot || !lot.location) return;

    const lotKey = `${lot.id}:${homeZip}`;
    if (window.__distanceCalculationsStarted?.has(lotKey)) {
      return;
    }

    const lotInfo = extractZipOrPostalCode(lot.location);
    if (!lotInfo) return;

    const cacheKey = `${homeZip}-${lotInfo.code}`;

    if (window.__distanceCache?.[cacheKey] !== undefined) {
      window.__distanceCalculationsStarted?.add(lotKey);
      return;
    }

    window.__distanceCalculationsStarted?.add(lotKey);
    if (window.__distanceCache) window.__distanceCache[cacheKey] = 'pending';

    calculateDistance(homeZip, lotInfo.code, saveToBackend)
      .then(function () {
        // Update distance displays in-place without full re-render
        updateDistanceDisplaysInPlace(homeZip, settings);
        // Also call legacy renderCallback if provided
        if (typeof renderCallback === 'function') {
          renderCallback();
        }
      })
      .catch(function (err) {
        log.error(`Failed: ${cacheKey}`, err);
      });
  });
}

/**
 * Format location with distance display
 */
export function formatLocationWithDistance(
  location: string,
  homeZip: string,
  settings: { distanceUnits?: string } | null | undefined
): string {
  if (!location) return '-';

  const displayLocation = escapeHtml(location);
  const cityStateZip = extractCityStateZip(location);
  let truncatedLocation = cityStateZip ? escapeHtml(cityStateZip) : displayLocation;

  if (!cityStateZip && displayLocation.length > 45) {
    truncatedLocation = displayLocation.substring(0, 45);
  }

  let distanceHtml = '';
  if (homeZip) {
    const lot = extractZipOrPostalCode(location);
    if (lot) {
      const cacheKey = `${homeZip}-${lot.code}`;
      if (typeof window.__distanceCache === 'undefined') {
        window.__distanceCache = {};
      }

      const cachedVal = window.__distanceCache[cacheKey];
      // Add data-distance-key attribute for in-place updates
      if (cachedVal === undefined) {
        distanceHtml = `<div class="distance-display distance-display--pending" data-distance-key="${cacheKey}">(...)</div>`;
      } else if (cachedVal === 'pending') {
        distanceHtml = `<div class="distance-display distance-display--pending" data-distance-key="${cacheKey}">(...)</div>`;
      } else if (typeof cachedVal === 'number') {
        const pref = getPreferredDistanceUnits(homeZip, settings);
        const displayVal = pref === 'km' ? Math.round(cachedVal * 1.60934) : cachedVal;
        const unit = pref === 'km' ? ' km' : ' mi';
        distanceHtml = `<div class="distance-display distance-display--resolved" data-distance-key="${cacheKey}">(~${displayVal}${unit})</div>`;
      }
    }
  }

  return `<div class="location-display" title="${displayLocation}">${truncatedLocation}</div>${distanceHtml}`;
}

/**
 * Update distance displays in-place without full re-render
 */
export function updateDistanceDisplaysInPlace(
  homeZip: string,
  settings: { distanceUnits?: string } | null | undefined
): void {
  if (!homeZip || typeof window.__distanceCache === 'undefined') return;

  const distanceElements = document.querySelectorAll<HTMLElement>(
    '.distance-display[data-distance-key]'
  );
  distanceElements.forEach(el => {
    const cacheKey = el.getAttribute('data-distance-key');
    if (!cacheKey) return;

    const cachedVal = window.__distanceCache[cacheKey];
    if (typeof cachedVal === 'number') {
      const pref = getPreferredDistanceUnits(homeZip, settings);
      const displayVal = pref === 'km' ? Math.round(cachedVal * 1.60934) : cachedVal;
      const unit = pref === 'km' ? ' km' : ' mi';
      el.textContent = `(~${displayVal}${unit})`;
      el.classList.remove('distance-display--pending');
      el.classList.add('distance-display--resolved');
    }
  });
}
